/**
 * THE print/PDF port — the one interface both platforms implement.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * DNR Vyapar 2.0 ships as a PWA and as a Windows desktop app. Printing works
 * very differently on the two, and the temptation is to let that difference
 * leak upward: an `if (isElectron)` in a screen, a feature that quietly does
 * nothing in the browser, a "Save as PDF" button that appears on one target and
 * not the other. Each of those is a bug waiting for a user on the other target.
 *
 * So the capabilities are STATED, not assumed, and the caller asks.
 *
 * WHAT EACH PLATFORM CAN ACTUALLY DO
 * ----------------------------------
 *                        PWA / browser        Electron (Windows)
 *   interactive print     yes (window.print)   yes
 *   silent, path PDF      NO                   yes (printToPDF)
 *
 * The asymmetry is real and must not be papered over. A browser cannot produce
 * a PDF without either the user choosing "Save as PDF" in the print dialog, or
 * a JS PDF library re-implementing layout. The second is unacceptable here: this
 * project exists because the millimetre geometry must survive, and jsPDF would
 * discard the very HTML that produces it. So the browser port reports
 * `silentPdf: false` and the UI offers Print -> Save as PDF instead. That is a
 * worse experience, and pretending otherwise would be worse still.
 *
 * WHY THE HTML CROSSES THE PORT UNTOUCHED
 * ---------------------------------------
 * `PrintableDocument.html` is already the finished document. Neither adapter may
 * transform it or re-render it. If Electron and the browser each generated their
 * own markup the two targets would drift — and the 1.6.x bug was precisely a
 * case of two renderers disagreeing about a printed page.
 */
import type { NormalizedDocument } from '../../core/doc/types';
import type { BusinessIdentity } from '../../core/doc/renderA4';
import { renderA4 } from '../../core/doc/renderA4';
import { renderPreview } from '../../core/doc/preview';
import { renderThermal, type RollWidth } from '../../core/doc/thermal';

/** What the host can actually do. Read this; do not assume. */
export interface PrintCapabilities {
  readonly platform: 'web' | 'electron';
  /** Opens the system print dialog. Available on both. */
  readonly interactivePrint: true;
  /**
   * Writes a PDF straight to a chosen path with no dialog.
   * FALSE on the web, and that is a product fact, not a bug to work around.
   */
  readonly silentPdf: boolean;
  /**
   * Talks to a thermal printer directly (ESC/POS over USB/serial).
   * Not implemented on either target yet. Declared so a caller can ask rather
   * than assume, and so the eventual implementation has a slot to fill.
   */
  readonly directThermal: boolean;
}

export type PrintTarget = 'a4' | 'thermal';

/** A finished document, ready to be handed to a printer. Never re-rendered. */
export interface PrintableDocument {
  /** The complete HTML. Adapters pass it through byte for byte. */
  readonly html: string;
  /** Suggested file name, without an extension. */
  readonly fileName: string;
  readonly target: PrintTarget;
  /** Paper width. Thermal only; undefined for A4. */
  readonly mediaWidthMm?: number;
}

export interface PrintRequest {
  readonly document: PrintableDocument;
  /** How many copies. Thermal only; ignored for A4. */
  readonly copies?: number;
}

export interface PdfRequest {
  readonly document: PrintableDocument;
  /** Overrides the suggested name. */
  readonly fileName?: string;
}

export interface PrintResult {
  /** Which adapter actually did the work. */
  readonly via: 'browser-dialog' | 'electron-print';
  /** False if the user dismissed the dialog without printing. */
  readonly printed: boolean;
}

export interface PdfResult {
  readonly via: 'electron-silent-pdf';
  /** False if the user cancelled the save dialog. */
  readonly saved: boolean;
  readonly filePath?: string;
}

export class PrintPortError extends Error {
  constructor(
    readonly code:
      | 'PDF_UNAVAILABLE_ON_WEB'
      | 'ELECTRON_BRIDGE_MISSING'
      | 'PRINT_CANCELLED'
      | 'PRINT_FAILED',
    message: string,


  ) {
    super(message);
    this.name = 'PrintPortError';
  }
}

/**
 * The single interface. A screen that wants to print depends on this and
 * nothing else — not on `window`, not on Electron, not on which target it runs
 * on.
 */
export interface PrintPort {
  readonly capabilities: PrintCapabilities;
  print(request: PrintRequest): Promise<PrintResult>;
  /** @throws PrintPortError `PDF_UNAVAILABLE_ON_WEB` on the browser target. */
  savePdf(request: PdfRequest): Promise<PdfResult>;
}
// ── Building the payload. This half is SHARED by both platforms. ─────────────
//
// Neither adapter builds HTML. They receive it finished. That is what keeps the
// two targets from drifting: there is exactly one place a document becomes
// markup, and both platforms are downstream of it.

/** A file-name-safe stem for a document. */
function fileStem(biz: BusinessIdentity, m: NormalizedDocument): string {
  return `${biz.legalName}-${m.docNumber}`.replace(/[^\w.-]+/g, '-');
}

/** Build an A4 document for printing. Identical bytes on every platform. */
export function printableA4(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  assetsBaseUrl = '',
): PrintableDocument {
  return {
    html: renderA4(m, biz, {}, assetsBaseUrl),
    fileName: fileStem(biz, m),
    target: 'a4',
  };
}

/** Build a thermal roll document. Identical bytes on every platform. */
export function printableThermal(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  opt: {
    mediaWidthMm: RollWidth;
    printableWidthMm?: number;
    copies?: number;
    assetsBaseUrl?: string;
  },
): PrintableDocument {
  return {
    html: renderThermal(m, biz, {
      mediaWidthMm: opt.mediaWidthMm,
      printableWidthMm: opt.printableWidthMm,
      copies: opt.copies,
      assetsBaseUrl: opt.assetsBaseUrl,
    }),
    fileName: fileStem(biz, m),
    target: 'thermal',
    mediaWidthMm: opt.mediaWidthMm,
  };
}

/**
 * Build a preview document.
 *
 * Expressed as a `PrintableDocument` so the same type flows to the screen and
 * to the printer, but it is NOT a print payload: `target` is still 'a4' and
 * the caller must not hand this to `print()`.
 */
export function previewDocument(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  assetsBaseUrl = '',
  scale = 1,
): PrintableDocument {
  return {
    html: renderPreview(m, biz, { scale }, assetsBaseUrl),
    fileName: m.docNumber,
    target: 'a4',
  };
}
